import { useIntl } from '@capsicum/common';
import { Button, CondensedButton } from '@capsicum/common-web';
import { MinusIcon, PlusIcon } from '@capsicum/common-web/icons';
import { type SellEntrypoint } from '@capsicum/socios-core';
import { useSetAtom } from 'jotai';
import { type RefObject, useEffect } from 'react';
import styled from 'styled-components';

import { buyButtonLoadingAtom, isReadyBuyButtonAtom } from '../../../partners/atoms/buyButtonAtoms';
import { useBuySellButtons } from '../../hooks/useBuySellButtons';

export type BuySellButtonsProps = {
	buyButtonRef?: RefObject<HTMLButtonElement>;
	condensed?: boolean;
	entrypoint: SellEntrypoint;
	fanTokenCurrencyId?: string;
	onClickBuy?: (isBuyEnabled?: boolean) => void;
	onClickBuyEvent?: () => void;
	onClickSell?: (isSellEnabled?: boolean) => void;
	partnerId?: string;
	testID?: string;
	withIcons?: boolean;
};

export const BuySellButtonsSkeleton = ({ condensed = false }) => (
	<S.Container>
		{condensed ? (
			<>
				<CondensedButton
					color="brand"
					disabled
					fullWidth
					isLoading
					size="small"
					testID="buy-button-skeleton"
					textSize="small"
				/>
				<CondensedButton
					color="brand"
					disabled
					fullWidth
					isLoading
					size="small"
					testID="sell-button-skeleton"
					textSize="small"
				/>
			</>
		) : (
			<>
				<Button disabled fullWidth isLoading testID="buy-button-skeleton" />
				<Button disabled fullWidth isLoading testID="sell-button-skeleton" />
			</>
		)}
	</S.Container>
);

/**
 * @description
 * This component is used to render buy and sell buttons on the web.
 * It encapsulates the logic of enabling and disabling the buttons.
 * Can be plugged in anywhere in the app.
 */
export const BuySellButtons = ({
	buyButtonRef,
	condensed = false,
	entrypoint,
	fanTokenCurrencyId,
	onClickBuy,
	onClickBuyEvent,
	onClickSell,
	partnerId,
	testID = 'marketplace',
	withIcons = true
}: BuySellButtonsProps) => {
	const { $t } = useIntl();
	const setIsReadyBuyButton = useSetAtom(isReadyBuyButtonAtom);
	const setBuyButtonLoading = useSetAtom(buyButtonLoadingAtom);

	const { buy, isBuyEnabled, isLoading, isMarketAvailable, isSellEnabled, sell } = useBuySellButtons(
		fanTokenCurrencyId || ''
	);

	useEffect(() => {
		if (isMarketAvailable) setIsReadyBuyButton(true);

		setBuyButtonLoading(isLoading);

		return () => {
			setIsReadyBuyButton(false);
			setBuyButtonLoading(true);
		};
	}, [isLoading, isMarketAvailable, setBuyButtonLoading, setIsReadyBuyButton]);

	if (!isMarketAvailable) return null;

	const onBuyClicked = () => {
		onClickBuy?.(isBuyEnabled);
		onClickBuyEvent?.();

		buy();
	};

	const buyButtonProps = {
		disabled: !isBuyEnabled,
		fullWidth: true,
		iconStart: withIcons ? { icon: { src: PlusIcon } } : undefined,
		onClick: onBuyClicked,
		ref: buyButtonRef,
		testID: `${testID}BuyButton${partnerId}-button`,
		text: $t('wallet.partnerDetails.marketplace.buy')
	};

	const sellButtonProps = {
		disabled: !isSellEnabled,
		fullWidth: true,
		iconStart: withIcons ? { icon: { src: MinusIcon } } : undefined,
		onClick: () => {
			onClickSell?.(isSellEnabled);
			sell(entrypoint);
		},
		testID: `${testID}SellButton${partnerId}-button`,
		text: $t('wallet.partnerDetails.marketplace.sell')
	};

	if (isLoading) {
		return <BuySellButtonsSkeleton condensed={condensed} />;
	}

	return (
		<S.Container>
			{condensed ? (
				<>
					<CondensedButton
						color="brand"
						{...buyButtonProps}
						key={`buy-${partnerId}`}
						size="small"
						textSize="small"
					/>

					<CondensedButton
						color="brand"
						{...sellButtonProps}
						key={`sell-${partnerId}`}
						size="small"
						textSize="small"
					/>
				</>
			) : (
				<>
					<Button {...buyButtonProps} key={`buy-${partnerId}`} />
					<Button {...sellButtonProps} key={`sell-${partnerId}`} />
				</>
			)}
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		position: relative;
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};

		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => theme.spacings.xsmall};
			@supports (padding-bottom: env(safe-area-inset-bottom)) {
				padding-bottom: max(env(safe-area-inset-bottom), 12px);
			}
		}
	`;
}
